import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ContentChild,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  OnDestroy,
  AfterViewChecked,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import { BrowserService } from '@core/browser.service';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { NgSelectSrValueDirective } from '@shared/directives/ng-select-sr-value.directive';
import { Subscription } from 'rxjs';

export type SelectTheme = 'default' | 'dark';

/**
 * Reusable ng-select component that wraps the ng-select library with consistent styling
 * and accessibility features. This component implements ControlValueAccessor to work
 * seamlessly with Angular forms.
 *
 * @example
 * Basic usage:
 * ```html
 * <zbp-select
 *   labelForId="unit-input"
 *   [items]="units"
 *   [formControl]="unitFormControl"
 *   [isInvalid]="unitFormControl.touched && unitFormControl.invalid"
 *   [isDisabled]="unitFormControl.disabled"
 *   [isReadonly]="isMakingApiCall">
 * </zbp-select>
 * ```
 *
 * @example
 * With custom template:
 * ```html
 * <zbp-select
 *   labelForId="unit-input"
 *   [items]="units"
 *   [formControl]="unitFormControl">
 *   <ng-template #itemTemplate let-item="item">
 *     <span [innerHTML]="item.displayName"></span>
 *   </ng-template>
 * </zbp-select>
 * ```
 *
 * Documentation for ng-select: https://www.npmjs.com/package/@ng-select/ng-select
 */
@Component({
  selector: 'zbp-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  imports: [
    A11yModule,
    CommonModule,
    FormsModule,
    NgSelectModule,
    ReactiveFormsModule,
    NgSelectSrValueDirective
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true
    }
  ]
})
export class SelectComponent implements ControlValueAccessor, AfterViewChecked, AfterViewInit, OnDestroy, OnChanges {
  /**
   * FormControl to bind to the select.
   * Used for the accessibility directive.
   *
   * NOTE: If your form control isn't binding, try importing ReactiveFormsModule in your component.
   */
  @Input() formControl?: FormControl;

  /**
   * Unique identifier for the select input element.
   * Used to connect the label's 'for' attribute to the input.
   *
   * A label's 'for' value would be provided here.
   */
  @Input() labelForId: string = '';

  /** If more description is needed than only the label, this can be provided. */
  @Input() ariaDescribedBy: string;

  /**
   * Unique identifier for error state label.
   *
   * Used for screen readers to announce the error message.
   */
  @Input() errorStateLabelId: string = '';

  /** Array of items to display in the dropdown. */
  @Input() items: any[] = [];

  /**
   * Property name to use as the display label for each item.
   * If not provided, the entire item will be used as the label.
   */
  @Input() bindLabel?: string;

  /**
   * Property name to use as the value for each item.
   * If not provided, the entire item will be used as the value.
   */
  @Input() bindValue?: string;

  /** Placeholder text to display when no item is selected. */
  @Input() placeholder: string = 'Select...';

  /**
   * Whether the select should display validation error styling. Defaults to 'touched' and 'invalid'.
   *
   * If validation is not necessary, can pass in 'false'
   */
  @Input() isInvalid: boolean = this.formControl?.touched && this.formControl?.invalid;

  /** Whether the select is disabled. */
  @Input() isDisabled: boolean = false;

  /** Whether the select is readonly (prevents opening but allows focus). */
  @Input() isReadonly: boolean = false;

  /**
   * CSS selector for the element to append the dropdown panel to.
   * Useful for modals to ensure proper z-index stacking.
   * @example appendTo=".modal"
   * @deprecated - causes alignment issues when resizing when the modal is open
   */
  @Input() appendTo: string;

  /** Whether to allow clearing the selected value. */
  @Input() clearable: boolean = false;

  /** Clear all text to be displayed. */
  @Input() clearAllText?: string;

  /** Whether to allow searching/filtering items. */
  @Input() searchable: boolean = false;

  /** Additional CSS classes to apply to the ng-select element for customizations */
  @Input() customClass: string = '';

  /** Select container will maintain full width of longest option in dropdown. */
  @Input() matchOptionWidth: boolean = true;

  /**
   * Custom focus color if needed for specific use-case.
   * Default is black.
   */
  @Input() focusColor: 'black' | 'focusBlue' = 'black';

  @Input() theme: SelectTheme = 'default';

  /** Emits when the selected value changes. */
  @Output() valueChange = new EventEmitter<any>();

  /** Emits when the selected value clears. */
  @Output() clear = new EventEmitter<any>();

  /**
   * Custom template for rendering both the selected item label and dropdown options.
   * Use with ng-template and #itemTemplate.
   * Used for both ng-label-tmp and ng-option-tmp directives from ng-select.
   */
  @ContentChild('itemTemplate') itemTemplate?: TemplateRef<any>;

  /**
   * Reference to the internal ng-select component.
   */
  @ViewChild(NgSelectComponent) ngSelectComponent?: NgSelectComponent;

  private _value: any = null;

  private observer: MutationObserver;

  private onChange: (value: any) => void = () => {
  };

  private onTouched: () => void = () => {
  };

  private subscriptions = new Subscription();

  constructor(protected browserService: BrowserService) {
  }

  get value(): any {
    return this._value;
  }

  /** Sets the value and notifies Angular forms. */
  set value(val: any) {
    if (val !== this._value) {
      this._value = val;
      this.onChange(val);
    }
  }

  writeValue(value: any): void {
    this._value = value;
  }

  /** Registers a callback function that should be called when the value changes. */
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /** Registers a callback function that should be called when the element is touched. */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Sets the disabled state of the ng-select (called by Angular forms).
   * @param isDisabled - Whether the ng-select should be disabled
   */
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  /** Handles value changes from the ng-select component. */
  onValueChange(value: any): void {
    this.value = value;
    this.onTouched();
    this.valueChange.emit(value);
  }

  onBlur(): void {
    this.onTouched();
  }

  open(): void {
    this.ngSelectComponent?.open();
  }

  close(): void {
    this.ngSelectComponent?.close();
  }

  focus(): void {
    this.ngSelectComponent?.focus();
  }

  ngAfterViewInit(): void {
    if (this.ngSelectComponent) {
      this.subscriptions.add(
        this.ngSelectComponent.closeEvent.subscribe(() => {
          // Return focus to the select element
          setTimeout(() => {
            this.ngSelectComponent?.focus();
          });
        })
      );
    }

    // Calculate the width of the container if specified.
    if (this.matchOptionWidth && this.items?.length) {
      this.calculateWidthOfSelect();
    }
  }

  ngAfterViewChecked(): void {
    if (!this.ngSelectComponent?.element) return;

    const selectLabel = this.ngSelectComponent?.element.querySelector('.ng-value-label');

    if (selectLabel && !selectLabel?.hasAttribute('aria-hidden')) {
      selectLabel.setAttribute('aria-hidden', 'true');
    }

    /* On iOS devices, VoiceOver ignores inputs that are marked as readonly. The readonly attribute comes from
    *  being marked as "searchable=false"
    * */
    if (this.browserService.isIOSDevice && !this.searchable) {
      const selectInput = this.ngSelectComponent?.element.querySelector('input');

      if (selectInput && selectInput?.hasAttribute('readonly')) {
        selectInput.removeAttribute('readonly');
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Calculate the width of the container if specified and items changed.
    if (changes.items && this.matchOptionWidth && this.items?.length) {
      this.calculateWidthOfSelect();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Sets the width on the select to be full-width as its largest option (including placeholder)
   * Prevents auto-resizing of the select when a selected option is shorter than a longer option.
   *
   * Calculation includes all the CSS properties in the select.
   */
  private calculateWidthOfSelect() {
    const selectElement = this.ngSelectComponent?.element;
    const container = selectElement?.querySelector('.ng-select-container');

    if (container) {
      const valueContainer = container.querySelector('.ng-value-container');
      const arrowWrapper = container.querySelector('.ng-arrow-wrapper');
      const computedStyle = window.getComputedStyle(container);
      const valueComputedStyle = valueContainer ? window.getComputedStyle(valueContainer) : null;

      // Calculate all spacing from option container
      if (computedStyle) {
        const paddingLeft = parseFloat(computedStyle.paddingLeft);
        const paddingRight = parseFloat(computedStyle.paddingRight);
        const borderLeft = parseFloat(computedStyle.borderLeftWidth);
        const borderRight = parseFloat(computedStyle.borderRightWidth);

        // Calculate spacing as floats from value container if it exists
        const valuePaddingLeft = valueComputedStyle ? parseFloat(valueComputedStyle.paddingLeft) : 0;
        const valuePaddingRight = valueComputedStyle ? parseFloat(valueComputedStyle.paddingRight) : 0;

        const arrowWidth = arrowWrapper ? (arrowWrapper as HTMLElement).offsetWidth : 0;

        // Get the measurement for the widest option with all relevant text styles
        // that will be used to configure width of select.

        /** Temporary span added to set the width of the select. Removed after width is set on select. */
        const tempSpacingElement = document.createElement('span');
        tempSpacingElement.style.cssText = 'visibility:hidden;position:absolute;white-space:nowrap';
        tempSpacingElement.style.font = computedStyle.font;
        tempSpacingElement.style.fontWeight = computedStyle.fontWeight;
        tempSpacingElement.style.fontSize = computedStyle.fontSize;
        tempSpacingElement.style.fontFamily = computedStyle.fontFamily;
        tempSpacingElement.style.letterSpacing = computedStyle.letterSpacing;
        tempSpacingElement.style.textTransform = computedStyle.textTransform;
        document.body.appendChild(tempSpacingElement);

        /** Will be set to the longest option in the list to help calculate width of the select container. */
        let maxWidth = 0;

        // Measure placeholder
        if (this.placeholder?.length) {
          tempSpacingElement.textContent = this.placeholder;
          maxWidth = tempSpacingElement.offsetWidth;
        }

        this.items.forEach((item) => {
          // Find the option that has the highest width.
          tempSpacingElement.textContent = this.bindLabel ? item[this.bindLabel] : item;
          maxWidth = Math.max(maxWidth, tempSpacingElement.offsetWidth);
        });

        // Remove the temporary element once width has been set.
        document.body.removeChild(tempSpacingElement);

        /** Total width with spacing of all components and styling, plus extra buffer */
        const totalWidth = maxWidth + paddingLeft + paddingRight + valuePaddingLeft + valuePaddingRight
          + borderLeft + borderRight + arrowWidth + 50;
        selectElement.style.width = `${totalWidth}px`;
      }
    }

    this.observer?.disconnect();
  }
}
